import { Order } from '@spa/services/order'
import { Payment } from '@spa/store/modules/orderConfirm/types'
import { DateWithoutHour } from '../date-time/date-without-hour'

export class DeliveryConfigState {
  /**
   * 配送日（selectedDeliveryDate）は、選択しないあるいは選択不可の場合があるためnullable
   * 配送時間帯（selectedDeliveryTime）は、選択しない場合でもデフォルト値を設定しなければならないためnon-null
   * 詳細はこちら: https://github.com/my-color/front/pull/4959#pullrequestreview-606007153
   */
  constructor(
    readonly selectedDeliveryMethod: DeliveryMethodDetail,
    readonly selectedDeliveryDate: DateWithoutHour | null,
    readonly selectedDeliveryTime: DeliveryTime,
    readonly selectedOkihaiPlace: OkihaiPlace | null,
    readonly context: DeliveryConfigContext,
    readonly deliveryMethods: DeliveryMethod[]
  ) {}

  // NOTE: 配送方法を変更した場合は配送日時と置き配の場所も再選択しなければならない
  changeDeliveryMethod(method: DeliveryMethodDetail): DeliveryConfigState {
    return new DeliveryConfigState(
      method,
      null,
      this.selectDefaultDeliveryTime(method),
      null,
      this.context,
      this.deliveryMethods
    )
  }

  /**
   * 設計として、配送日ごとに配送時間帯リストを持っている。
   * つまり配送日によって、選択可能な配送時間帯が異なることを許容している。
   * したがって配送日を変更した場合は配送時間帯を初期化して再選択とする。
   */
  changeDeliveryDate(date: DateWithoutHour | null): DeliveryConfigState {
    return new DeliveryConfigState(
      this.selectedDeliveryMethod,
      date,
      this.selectDefaultDeliveryTime(this.selectedDeliveryMethod),
      this.selectedOkihaiPlace,
      this.context,
      this.deliveryMethods
    )
  }

  changeDeliveryTime(deliveryTime: DeliveryTime): DeliveryConfigState {
    return new DeliveryConfigState(
      this.selectedDeliveryMethod,
      this.selectedDeliveryDate,
      deliveryTime,
      this.selectedOkihaiPlace,
      this.context,
      this.deliveryMethods
    )
  }

  changeOkihaiPlace(okihaiPlace: OkihaiPlace): DeliveryConfigState {
    return new DeliveryConfigState(
      this.selectedDeliveryMethod,
      this.selectedDeliveryDate,
      this.selectedDeliveryTime,
      okihaiPlace,
      this.context,
      this.deliveryMethods
    )
  }

  /**
   * 配送時間帯の初期値はエンジンまたは配送業者に依存するため、MCフロントで持つべき知識では無い。
   * しかし、現状（2021.03.08）ではMCフロント側で持たざるを得ない。
   * また現在のモデル設計の制約として、配送方法によっては配送時間帯初期値を知らないケース
   * がある（ネコポスの場合、または何らかの理由で配送日選択不可の場合）。
   * その場合はクライアント側で静的に持った初期値を設定する。
   * 詳細はこちら: https://github.com/my-color/front/pull/4959#pullrequestreview-606007153
   */
  private selectDefaultDeliveryTime(
    method: DeliveryMethodDetail,
    fallback: () => DeliveryTime = getDefaultDeliveryTime
  ): DeliveryTime {
    return method.deliveryDateTimes.length ? method.deliveryDateTimes[0].defaultTime : fallback()
  }
}

export interface DeliveryConfigContext {
  payment: Payment
  cartIds: string[]
  deliveryId: string
}

export interface DeliveryMethod {
  id: string
  name: string
  externalId: string
  description: string
}

export interface DeliveryMethodDetail extends DeliveryMethod {
  deliveryDateTimes: DeliveryDateTime[]
  okihaiPlaces: OkihaiPlace[]
}

export interface DeliveryDateTime {
  date: DateWithoutHour
  timeList: DeliveryTime[]
  defaultTime: DeliveryTime
}
export interface DeliveryTime {
  id: string
  label: string
}
export interface OkihaiPlace {
  id: string
  label: string
}

/**
 * この値はエンジンあるいは配送業者に依存するため、外から注入するような構造が望ましい。
 * しかし、現状（2021.03.08）ではMCフロント側で持たざるを得ないケースがある。
 * 詳細はこちら: https://github.com/my-color/front/pull/4959#pullrequestreview-606007153
 */
export const getDefaultDeliveryTime = (): DeliveryTime => {
  return {
    id: '0',
    label: '指定なし',
  }
}

export interface DeliveryConfigSpecification {
  readonly configList: DeliveryConfigState[]

  replace(config: DeliveryConfigState): DeliveryConfigSpecification

  /**
   * 購入処理中、 #findByOrderId を使って処理対象の注文に対応する配送方法を取得可能にする想定だった.
   *
   * 現状(at 2021/02/19)の実装が「配送方法は全体で一つのみ」という前提に強く結びついており、
   * 1stリリースの段階でそこを分解するのは一旦あきらめ、既存の仕組み(vuexで一つの配送設定のみ管理)に乗ることにした.
   *
   * 今後、配送設定を拡張して 1配送設定/1注文 を吸収する or 吸収可能にする際は、
   * 下記のesaを参考ににしつつ、このメソッドを改めて検討・定義・実装する想定.
   *
   * @see https://my-color.esa.io/posts/485#%E3%82%B7%E3%83%BC%E3%82%B1%E3%83%B3%E3%82%B9-1
   */
  // findByOrderId(orderId: string): DeliveryConfig
}

/**
 * 初期値設定用の NullObject
 */
export const emptyDeliveryConfigSpecification: DeliveryConfigSpecification = {
  configList: [],

  replace(config: DeliveryConfigState): DeliveryConfigSpecification {
    /*
     * 本来なら、事前のコンテクストに応じて適切な仕様オブジェクトを返すべき.
     * 現時点(at 2021/02/19)では、他の仕様オブジェクトのバリエーションが特に無いため、
     * 一旦固定値返却で済ませている.
     */
    return new OnlySingleDeliveryConfig(config)
  },
}

export class OnlySingleDeliveryConfig implements DeliveryConfigSpecification {
  private current: DeliveryConfigState

  constructor(initialSetting: DeliveryConfigState) {
    this.current = initialSetting
  }

  get configList(): DeliveryConfigState[] {
    return [this.current]
  }

  replace(config: DeliveryConfigState): this {
    this.current = config

    return this
  }
}

interface DeliveryConfigSpecificationBuilder {
  build(
    orders: Order[],
    paramsList: [DeliveryConfigContext, ...DeliveryConfigContext[]]
  ): DeliveryConfigSpecification
}

export class OnlySingleDeliveryConfigBuilder implements DeliveryConfigSpecificationBuilder {
  constructor(
    private readonly deliveryMethod: DeliveryMethodDetail,
    private readonly deliveryDate: DateWithoutHour | null,
    private readonly deliveryTime: DeliveryTime,
    private readonly deliveryMethods: DeliveryMethod[]
  ) {}

  build(_o: Order[], paramsList: [DeliveryConfigContext, ...DeliveryConfigContext[]]) {
    return new OnlySingleDeliveryConfig(
      new DeliveryConfigState(
        this.deliveryMethod,
        this.deliveryDate,
        this.deliveryTime,
        (this.deliveryMethod.okihaiPlaces || [])[0] || null,
        paramsList[0], // 常に一つだけ必ず存在する
        this.deliveryMethods
      )
    )
  }
}
